import * as Sentry from "@sentry/react";
import { type AxiosError, isAxiosError } from "axios";

export const OOPS_TAG = "oops";
export const API_ERROR_TAG = "api_error";

// "redirect": error caught by a boundary that redirects instead of showing "Oops"
export type OopsSource = "page" | "prescriber-page" | "boundary" | "redirect";

// Expected API errors, not reported even when they lead to an "Oops" screen:
// 401 (redirected to login), 404 (missing resource), and no response at all (timeout, network error, cancellation)
const isExpectedApiError = (error: AxiosError) => {
  const status = error.response?.status;
  return status === undefined || status === 401 || status === 404;
};

/**
 * Reports an error that led to an "Oops" screen.
 * Tagged with `oops:<source>` so a Sentry alert rule can target these events,
 * and never dropped by the filters of beforeSend (see App.tsx).
 */
export const captureOopsError = (error: unknown, source: OopsSource, componentStack?: string | null) => {
  if (isAxiosError(error) && isExpectedApiError(error)) {
    return;
  }
  Sentry.captureException(error, {
    level: "fatal",
    tags: { [OOPS_TAG]: source },
    contexts: componentStack ? { react: { componentStack } } : undefined,
  });
};

class ApiError extends Error {
  name = "ApiError";
}

// "https://api.host/v2/candidate/65dc3ffda302fafdd296a975?x=1" -> "/v2/candidate/:id"
const normalizeApiPath = ({ config }: AxiosError) =>
  `${config?.baseURL ?? ""}${config?.url ?? ""}`
    .split("?")[0]
    .replace(/^https?:\/\/[^/]+/, "")
    .replace(/\/[0-9a-f]{24}(?=\/|$)/gi, "/:id");

/**
 * Reports 5xx and 403 API responses, grouped in Sentry by endpoint and status.
 */
export const captureApiError = (error: AxiosError) => {
  const statusCode = error.response?.status;
  if (statusCode === undefined || (statusCode < 500 && statusCode !== 403)) {
    return;
  }
  const method = error.config?.method?.toUpperCase() ?? "GET";
  const path = normalizeApiPath(error);
  const status = String(statusCode);

  Sentry.captureException(new ApiError(`${status} ${method} ${path}`, { cause: error }), {
    fingerprint: ["api-error", method, path, status],
    tags: { [API_ERROR_TAG]: status, "api.method": method, "api.path": path },
  });
};

const CHUNK_LOAD_ERROR = /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

// Lazy chunk missing, usually because a new version was deployed since the page was loaded
export const isChunkLoadError = (error: unknown) => error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);

interface ISentryUser {
  _id: string;
  organisation: { _id: string; name: string };
  roleV2: { value: string };
}

export const setSentryUser = (user: ISentryUser) => {
  Sentry.setUser({ id: user._id });
  Sentry.setTags({
    organisation: user.organisation.name,
    organisation_id: user.organisation._id,
    role: user.roleV2.value,
  });
};

// Logout navigates without reloading the page: the previous user would stay on the next events
export const clearSentryUser = () => {
  Sentry.setUser(null);
  Sentry.setTags({ organisation: undefined, organisation_id: undefined, role: undefined });
};
